// SPDX-License-Identifier: AGPL-3.0-or-later

// Full-card animated effect layered over the profile card (Discord "profile effect").
// Purely decorative: no pointer events, paused when the tab is hidden or motion is reduced.

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {createProfileFx, PROFILE_FX_KEYS, PROFILE_FX_TINTS} from '@app/features/cosmetics/fx/ProfileFx';
import {useGamePerformanceActive} from '@app/features/performance/state/GamePerformanceMode';
import ProfileEffectCatalog from '@app/features/user/state/ProfileEffectCatalog';
import Users from '@app/features/user/state/Users';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useRef} from 'react';

const reduceMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const layer: React.CSSProperties = {
	position: 'absolute',
	inset: 0,
	width: '100%',
	height: '100%',
	pointerEvents: 'none',
};

export function hasProfileFx(key: string | null | undefined): key is string {
	return Boolean(key && (PROFILE_FX_KEYS as ReadonlyArray<string>).includes(key));
}

export function ProfileFxLayer({
	fxKey,
	className,
	active = true,
}: {
	fxKey: string;
	className?: string;
	active?: boolean;
}) {
	const rootRef = useRef<HTMLDivElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const glRef = useRef<HTMLCanvasElement>(null);
	const engineRef = useRef<{frame: (dt: number) => void} | null>(null);
	const paused = useGamePerformanceActive();
	// The engine (canvases, sprites, WebGL) lives as long as the effect key. Hover only starts and
	// stops the loop; rebuilding it on every hover leaked a WebGL context and full-size buffers.
	useEffect(() => {
		const root = rootRef.current;
		const canvas = canvasRef.current;
		const gl = glRef.current;
		if (!root || !canvas || !gl) return;
		const engine = createProfileFx(root, canvas, gl);
		// Decorative layer: a drawing error must never take the page down with it.
		let broken = false;
		const safe = (fn: () => void) => {
			if (broken) return;
			try {
				fn();
			} catch {
				broken = true;
			}
		};
		safe(() => {
			engine.resize();
			engine.setEffect(fxKey);
			// Warm up so idle previews show particles spread out.
			for (let i = 0; i < 90; i++) engine.frame(0.016);
		});
		const observer = new ResizeObserver(() => safe(() => engine.resize()));
		observer.observe(root);
		engineRef.current = {frame: (dt) => safe(() => engine.frame(dt))};
		return () => {
			engineRef.current = null;
			observer.disconnect();
			safe(() => engine.dispose());
		};
	}, [fxKey]);
	useEffect(() => {
		if (!active || paused || reduceMotion) return;
		let raf = 0;
		let last = performance.now();
		const loop = (now: number) => {
			raf = requestAnimationFrame(loop);
			const dt = Math.min(0.05, (now - last) / 1000);
			last = now;
			if (!document.hidden) engineRef.current?.frame(dt);
		};
		raf = requestAnimationFrame(loop);
		return () => cancelAnimationFrame(raf);
	}, [active, paused, fxKey]);
	const tint = (PROFILE_FX_TINTS as Record<string, React.CSSProperties>)[fxKey];
	return (
		<div
			ref={rootRef}
			className={className}
			aria-hidden
			style={{...layer, zIndex: 5, overflow: 'hidden', borderRadius: 'inherit'}}
		>
			{tint ? <div style={{...layer, ...tint}} /> : null}
			<canvas ref={glRef} style={{...layer, mixBlendMode: 'screen'}} />
			<canvas ref={canvasRef} style={layer} />
		</div>
	);
}

export const ProfileEffectOverlay = observer(function ProfileEffectOverlay({userId}: {userId: string}) {
	useEffect(() => {
		void ProfileEffectCatalog.load();
	}, []);
	const effectId = Users.getUser(userId)?.profileEffectId ?? null;
	const entry = ProfileEffectCatalog.getById(effectId);
	if (!RuntimeConfig.profileFramesEnabled || !entry || !hasProfileFx(entry.fx)) return null;
	return <ProfileFxLayer fxKey={entry.fx} />;
});
